import { useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { createChartScale, createDurationChartScale } from './chartScale';
import { buildCumulativePages } from './dailyChartData';

const VIEWBOX_WIDTH = 680;
const VIEWBOX_HEIGHT = 220;
const VOLUME_VIEWBOX_HEIGHT = 84;
const PLOT_TOP = 0;
const PLOT_BOTTOM = VIEWBOX_HEIGHT;
const MINUTE_MS = 60_000;

type ChartMode = 'pages' | 'time';

type DailyPagesComparisonChartProps = {
  referenceDate: Date;
  currentDailyPages: readonly number[];
  previousDailyPages: readonly number[];
  currentDailyDurationMs?: readonly number[];
  previousDailyDurationMs?: readonly number[];
  hasHistoricalPages?: boolean;
  maxReferenceDate?: Date;
  onReferenceMonthChange?: (date: Date) => void;
};

const daysInMonth = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const previousMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth() - 1, 1);

const normalizeDailyValues = (values: readonly number[], length: number, roundValues: boolean): number[] =>
  Array.from({ length }, (_, index) => {
    const value = values[index];
    if (!Number.isFinite(value)) return 0;
    return roundValues ? Math.round(value ?? 0) : value ?? 0;
  });

const monthName = (date: Date): string => {
  const label = date.toLocaleDateString('pt-BR', { month: 'long' });
  return `${label.charAt(0).toLocaleUpperCase('pt-BR')}${label.slice(1)}`;
};

const formatNumber = (value: number): string => Math.round(value).toLocaleString('pt-BR');
const formatPages = (value: number): string => `${formatNumber(value)} ${value === 1 ? 'página' : 'páginas'}`;
const formatDifference = (value: number): string =>
  `${value > 0 ? '+' : value < 0 ? '-' : ''}${formatPages(Math.abs(value))}`;

const formatDurationMinutes = (value: number): string => {
  const totalMinutes = Math.max(0, Math.round(value));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

const formatDurationAxis = (value: number): string => {
  const totalMinutes = Math.max(0, Math.round(value));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h${minutes}`;
};

const formatDurationDifference = (value: number): string =>
  `${value > 0 ? '+' : value < 0 ? '-' : ''}${formatDurationMinutes(Math.abs(value))}`;

const differenceText = (current: number, previous: number, suffix: string): string => {
  const difference = current - previous;
  if (difference === 0) return `Mesmo total ${suffix}.`;
  return `${formatPages(Math.abs(difference))} ${difference > 0 ? 'acima' : 'abaixo'} ${suffix}.`;
};

const makePath = (
  values: readonly number[],
  startDay: number,
  domainDays: number,
  scaleMax: number
): string => values.map((value, index) => {
  const day = startDay + index;
  const x = domainDays <= 1 ? 0 : ((day - 1) / (domainDays - 1)) * VIEWBOX_WIDTH;
  const y = scaleMax > 0
    ? PLOT_BOTTOM - (Math.max(0, value) / scaleMax) * (PLOT_BOTTOM - PLOT_TOP)
    : PLOT_BOTTOM;
  return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
}).join(' ');

const xPosition = (day: number, domainDays: number): number =>
  domainDays <= 1 ? 0 : ((day - 1) / (domainDays - 1)) * VIEWBOX_WIDTH;

const yPosition = (value: number, scaleMax: number): number =>
  scaleMax > 0
    ? PLOT_BOTTOM - (Math.max(0, value) / scaleMax) * (PLOT_BOTTOM - PLOT_TOP)
    : PLOT_BOTTOM;

const volumeYPosition = (value: number, scaleMax: number): number =>
  scaleMax > 0
    ? VOLUME_VIEWBOX_HEIGHT - (Math.max(0, value) / scaleMax) * VOLUME_VIEWBOX_HEIGHT
    : VOLUME_VIEWBOX_HEIGHT;

const dayTicks = (domainDays: number): number[] =>
  Array.from({ length: domainDays }, (_, index) => index + 1);

const DailyPagesComparisonChartContent = ({
  referenceDate,
  currentDailyPages,
  previousDailyPages,
  currentDailyDurationMs = [],
  previousDailyDurationMs = [],
  hasHistoricalPages = false,
  maxReferenceDate,
  onReferenceMonthChange
}: DailyPagesComparisonChartProps) => {
  const currentMonthDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const previousMonthDate = previousMonth(currentMonthDate);
  const currentDays = daysInMonth(currentMonthDate);
  const previousDays = daysInMonth(previousMonthDate);
  const today = Math.min(Math.max(1, referenceDate.getDate()), currentDays);
  const comparisonDay = Math.min(today, previousDays);
  const domainDays = Math.max(currentDays, previousDays);
  const currentName = monthName(currentMonthDate);
  const previousName = monthName(previousMonthDate);
  const monthInputValue = `${currentMonthDate.getFullYear()}-${String(currentMonthDate.getMonth() + 1).padStart(2, '0')}`;
  const maxMonthInputValue = maxReferenceDate
    ? `${maxReferenceDate.getFullYear()}-${String(maxReferenceDate.getMonth() + 1).padStart(2, '0')}`
    : undefined;
  const [selectedDay, setSelectedDay] = useState(today);
  const [hoveredDay, setHoveredDay] = useState<number>();
  const [chartMode, setChartMode] = useState<ChartMode>('pages');
  const activePointerId = useRef<number | undefined>(undefined);
  const previewDay = hoveredDay ?? selectedDay;

  const isTimeMode = chartMode === 'time';
  const currentDailySource = isTimeMode
    ? currentDailyDurationMs.map((durationMs) => durationMs / MINUTE_MS)
    : currentDailyPages;
  const previousDailySource = isTimeMode
    ? previousDailyDurationMs.map((durationMs) => durationMs / MINUTE_MS)
    : previousDailyPages;
  const normalizedCurrentDaily = normalizeDailyValues(currentDailySource, currentDays, !isTimeMode);
  const currentCumulative = buildCumulativePages(normalizedCurrentDaily);
  const previousCumulative = buildCumulativePages(normalizeDailyValues(previousDailySource, previousDays, !isTimeMode));
  const currentVisible = currentCumulative.slice(0, today);
  const currentDailyVisible = normalizedCurrentDaily.slice(0, today);
  const scaleForMode = isTimeMode ? createDurationChartScale : createChartScale;
  const scale = scaleForMode([...currentVisible, ...previousCumulative]);
  const volumeScale = scaleForMode(currentDailyVisible);
  const currentAtComparison = currentCumulative[comparisonDay - 1] ?? 0;
  const previousAtComparison = previousCumulative[comparisonDay - 1] ?? 0;
  const currentTotal = currentVisible.at(-1) ?? 0;
  const previousTotal = previousCumulative.at(-1) ?? 0;
  const bothEmpty = currentTotal === 0 && previousTotal === 0;
  const inspectedPreviousDay = Math.min(previewDay, previousDays);
  const inspectedCurrent = currentCumulative[previewDay - 1] ?? 0;
  const inspectedPrevious = previousCumulative[inspectedPreviousDay - 1] ?? 0;
  const inspectedDifference = inspectedCurrent - inspectedPrevious;
  const inspectedDaily = currentDailyVisible[previewDay - 1] ?? 0;

  const pagesSummary = bothEmpty
    ? 'Ainda não há páginas registradas para comparar.'
    : currentAtComparison === 0 && previousAtComparison > 0
      ? `${previousName} tinha ${formatPages(previousAtComparison)} registradas até o dia ${comparisonDay}.`
      : currentAtComparison > 0 && previousAtComparison === 0
        ? `No mês passado não havia páginas registradas até o dia ${comparisonDay}.`
        : differenceText(currentAtComparison, previousAtComparison, `do mês passado até o dia ${comparisonDay}`);
  const timeSummary = bothEmpty
    ? 'Ainda não há tempo de leitura registrado para comparar.'
    : currentAtComparison === 0 && previousAtComparison > 0
      ? `${previousName} tinha ${formatDurationMinutes(previousAtComparison)} registrados até o dia ${comparisonDay}.`
      : currentAtComparison > 0 && previousAtComparison === 0
        ? `No mês passado não havia tempo de leitura registrado até o dia ${comparisonDay}.`
        : `${formatDurationMinutes(Math.abs(currentAtComparison - previousAtComparison))} ${currentAtComparison >= previousAtComparison ? 'acima' : 'abaixo'} do mês passado até o dia ${comparisonDay}.`;
  const summary = isTimeMode ? timeSummary : pagesSummary;
  const formatMetricValue = isTimeMode ? formatDurationMinutes : formatPages;
  const formatMetricDifference = isTimeMode ? formatDurationDifference : formatDifference;
  const formatAxisValue = isTimeMode ? formatDurationAxis : formatNumber;

  const selectNearestDay = (target: HTMLDivElement, clientX: number, preview: boolean) => {
    const bounds = target.getBoundingClientRect();
    const position = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
    const day = Math.min(today, Math.max(1, Math.round(position * (domainDays - 1)) + 1));
    if (preview) setHoveredDay((current) => current === day ? current : day);
    else {
      setHoveredDay((current) => current === undefined ? current : undefined);
      setSelectedDay((current) => current === day ? current : day);
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    activePointerId.current = event.pointerId;
    selectNearestDay(event.currentTarget, event.clientX, false);
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture may be unavailable for synthetic events; selection still works on the plot.
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerId.current === event.pointerId) {
      selectNearestDay(event.currentTarget, event.clientX, false);
    } else if (event.pointerType === 'mouse') {
      selectNearestDay(event.currentTarget, event.clientX, true);
    }
  };

  const finishPointerSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerId.current !== event.pointerId) return;
    selectNearestDay(event.currentTarget, event.clientX, false);
    activePointerId.current = undefined;
    try {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // The browser may already have released capture while completing a gesture.
    }
  };

  const cancelPointerSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerId.current !== event.pointerId) return;
    activePointerId.current = undefined;
    try {
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // The browser may release capture before pointercancel is delivered.
    }
  };

  const clearMousePreview = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && activePointerId.current === undefined) setHoveredDay(undefined);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    selectNearestDay(event.currentTarget, event.clientX, false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const nextDay = event.key === 'ArrowLeft'
      ? Math.max(1, selectedDay - 1)
      : event.key === 'ArrowRight'
        ? Math.min(today, selectedDay + 1)
        : event.key === 'Home'
          ? 1
          : event.key === 'End'
            ? today
            : undefined;
    if (nextDay === undefined) return;
    event.preventDefault();
    setHoveredDay(undefined);
    setSelectedDay(nextDay);
  };

  const previousMain = previousCumulative.slice(0, comparisonDay);
  const previousAfterToday = previousCumulative.slice(Math.max(0, comparisonDay - 1));
  const ticks = dayTicks(domainDays);
  const barWidth = Math.min(12, (VIEWBOX_WIDTH / domainDays) * 0.48);
  const selectedDescription = isTimeMode
    ? [
        `Dia ${previewDay}`,
        `${currentName}: ${formatDurationMinutes(inspectedCurrent)} acumulados`,
        `${previousName}: ${formatDurationMinutes(inspectedPrevious)} acumulados${previewDay > previousDays ? ` até o último dia, ${previousDays}` : ''}`,
        `Diferença: ${formatDurationDifference(inspectedDifference)}`,
        `Tempo no dia: ${formatDurationMinutes(inspectedDaily)}`,
        summary
      ].join('. ')
    : [
        `Dia ${previewDay}`,
        `${currentName}: ${formatPages(inspectedCurrent)} acumuladas`,
        `${previousName}: ${formatPages(inspectedPrevious)} acumuladas${previewDay > previousDays ? ` até o último dia, ${previousDays}` : ''}`,
        `Diferença: ${formatDifference(inspectedDifference)}`,
        `Volume no dia: ${formatPages(inspectedDaily)}`,
        summary
      ].join('. ');

  return (
    <section className="daily-chart-section" aria-labelledby="daily-chart-section-title">
      <header className="daily-chart-section__header">
        <div className="daily-chart-section__title">
          <h2 id="daily-chart-section-title">
            <label className="daily-chart__period-picker">
              <span>{currentName} de {currentMonthDate.getFullYear()}</span>
              <input
                type="month"
                aria-label="Mês do gráfico diário"
                value={monthInputValue}
                max={maxMonthInputValue}
                onChange={(event) => {
                  const [year, month] = event.target.value.split('-').map(Number);
                  if (year && month) onReferenceMonthChange?.(new Date(year, month - 1, 1));
                }}
              />
            </label>
          </h2>
          <i aria-hidden="true">·</i><b>Dia {previewDay}</b>
        </div>
        <div className="daily-chart-mode-switch" role="group" aria-label="Métrica do gráfico diário">
          <button type="button" className={chartMode === 'pages' ? 'is-active' : ''} aria-pressed={chartMode === 'pages'} onClick={() => setChartMode('pages')}>Páginas</button>
          <button type="button" className={chartMode === 'time' ? 'is-active' : ''} aria-pressed={chartMode === 'time'} onClick={() => setChartMode('time')}>Tempo</button>
        </div>
      </header>

      <div className={`chart-card daily-chart-card${isTimeMode ? ' is-time-mode' : ''}`}>
        <div className="daily-chart__header">
          <div className="daily-chart__details" aria-live="polite" aria-atomic="true">
            <div className="daily-chart__metrics">
              <div className="daily-chart__metric daily-chart__metric--previous">
                <span>{previousName}</span>
                <strong>{formatMetricValue(inspectedPrevious)}</strong>
              </div>
              <div className="daily-chart__metric daily-chart__metric--current">
                <span>{currentName}</span>
                <strong>{formatMetricValue(inspectedCurrent)}</strong>
              </div>
              <div className="daily-chart__metric daily-chart__metric--difference">
                <span>Diferença</span>
                <strong>{formatMetricDifference(inspectedDifference)}</strong>
              </div>
            </div>
            <span className="sr-only">{isTimeMode ? 'Tempo' : 'Volume'} no dia: {formatMetricValue(inspectedDaily)}</span>
          </div>
        </div>

        <div
          className="daily-chart"
          role="group"
          aria-label={isTimeMode
            ? `Leitura de ${currentName.toLocaleLowerCase('pt-BR')} de ${currentMonthDate.getFullYear()}: tempo acumulado comparado com ${previousName.toLocaleLowerCase('pt-BR')} e tempo diário`
            : `Leitura de ${currentName.toLocaleLowerCase('pt-BR')} de ${currentMonthDate.getFullYear()}: páginas acumuladas comparadas com ${previousName.toLocaleLowerCase('pt-BR')} e volume diário`}
        >
        <div className="daily-chart__visual">
          <div className="daily-chart__axis" aria-hidden="true">
            {scale.ticks.map((tick) => (
              <span key={tick} style={{ bottom: scale.scaleMax ? `${(tick / scale.scaleMax) * 100}%` : '0%' }}>
                {formatAxisValue(tick)}
              </span>
            ))}
          </div>

          <div
            className="daily-chart__plot"
            role="slider"
            tabIndex={0}
            aria-label="Dia inspecionado no gráfico"
            aria-valuemin={1}
            aria-valuemax={today}
            aria-valuenow={previewDay}
            aria-valuetext={selectedDescription}
            data-selected-day={previewDay}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishPointerSelection}
            onPointerCancel={cancelPointerSelection}
            onPointerLeave={clearMousePreview}
            onClick={handleClick}
          >
            {scale.ticks.map((tick) => (
              <span
                key={tick}
                className={`daily-chart__gridline${tick === 0 ? ' daily-chart__gridline--zero' : ''}`}
                style={{ bottom: scale.scaleMax ? `${(tick / scale.scaleMax) * 100}%` : '0%' }}
                data-daily-gridline
                aria-hidden="true"
              />
            ))}

            <span
              className={`daily-chart__today${today === 1 ? ' is-at-start' : ''}${today === domainDays ? ' is-at-end' : ''}`}
              style={{ left: `${(xPosition(today, domainDays) / VIEWBOX_WIDTH) * 100}%` }}
              data-today-day={today}
              aria-hidden="true"
            ><span>Hoje</span></span>

            <span
              className="daily-chart__selection"
              style={{ left: `${(xPosition(previewDay, domainDays) / VIEWBOX_WIDTH) * 100}%` }}
              data-daily-selection-day={previewDay}
              aria-hidden="true"
            />

            <svg viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
              {!bothEmpty && (
                <>
                  <path
                    className="daily-chart__line daily-chart__line--previous"
                    d={makePath(previousMain, 1, domainDays, scale.scaleMax)}
                    vectorEffect="non-scaling-stroke"
                    data-series="previous"
                    data-end-day={comparisonDay}
                  />
                  {previousDays > comparisonDay && (
                    <path
                      className="daily-chart__line daily-chart__line--previous-tail"
                      d={makePath(previousAfterToday, comparisonDay, domainDays, scale.scaleMax)}
                      vectorEffect="non-scaling-stroke"
                      data-series="previous-after-today"
                      data-end-day={previousDays}
                    />
                  )}
                  <path
                    className="daily-chart__line daily-chart__line--current"
                    d={makePath(currentVisible, 1, domainDays, scale.scaleMax)}
                    vectorEffect="non-scaling-stroke"
                    data-series="current"
                    data-end-day={today}
                  />
                </>
              )}
              <circle
                className="daily-chart__point daily-chart__point--previous"
                cx={xPosition(inspectedPreviousDay, domainDays)}
                cy={yPosition(inspectedPrevious, scale.scaleMax)}
                r="4"
                vectorEffect="non-scaling-stroke"
              />
              <circle
                className="daily-chart__point daily-chart__point--current"
                cx={xPosition(previewDay, domainDays)}
                cy={yPosition(inspectedCurrent, scale.scaleMax)}
                r="4.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

          </div>
        </div>

        <div className="daily-volume-chart">
          <div className="daily-volume-chart__visual">
            <div className="daily-chart__axis daily-volume-chart__axis" aria-hidden="true">
              {volumeScale.ticks.map((tick) => (
                <span key={tick} style={{ bottom: volumeScale.scaleMax ? `${(tick / volumeScale.scaleMax) * 100}%` : '0%' }}>
                  {formatAxisValue(tick)}
                </span>
              ))}
            </div>

            <div
              className="daily-volume-chart__plot"
              data-domain-days={domainDays}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={finishPointerSelection}
              onPointerCancel={cancelPointerSelection}
              onPointerLeave={clearMousePreview}
              onClick={handleClick}
            >
              {volumeScale.ticks.map((tick) => (
                <span
                  key={tick}
                  className={`daily-chart__gridline${tick === 0 ? ' daily-chart__gridline--zero' : ''}`}
                  style={{ bottom: volumeScale.scaleMax ? `${(tick / volumeScale.scaleMax) * 100}%` : '0%' }}
                  aria-hidden="true"
                />
              ))}

              <span
                className="daily-volume-chart__selection"
                style={{ left: `${(xPosition(previewDay, domainDays) / VIEWBOX_WIDTH) * 100}%` }}
                data-volume-selection-day={previewDay}
                aria-hidden="true"
              />

              <svg viewBox={`0 0 ${VIEWBOX_WIDTH} ${VOLUME_VIEWBOX_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
                {currentDailyVisible.map((value, index) => {
                  if (value <= 0) return null;
                  const day = index + 1;
                  const y = volumeYPosition(value, volumeScale.scaleMax);
                  return (
                    <rect
                      key={day}
                      className={`daily-volume-chart__bar${previewDay === day ? ' is-selected' : ''}`}
                      x={xPosition(day, domainDays) - barWidth / 2}
                      y={y}
                      width={barWidth}
                      height={VOLUME_VIEWBOX_HEIGHT - y}
                      rx={Math.min(3, barWidth / 2)}
                      vectorEffect="non-scaling-stroke"
                      data-daily-volume-bar
                      data-day={day}
                      data-value={value}
                      data-x-position={xPosition(day, domainDays)}
                    />
                  );
                })}
              </svg>

              {Math.round(inspectedDaily) > 0 && (
                <span
                  className={`daily-volume-chart__value${previewDay === 1 ? ' is-at-start' : ''}${previewDay === domainDays ? ' is-at-end' : ''}`}
                  style={{
                    left: `${(xPosition(previewDay, domainDays) / VIEWBOX_WIDTH) * 100}%`,
                    bottom: volumeScale.scaleMax ? `calc(${(inspectedDaily / volumeScale.scaleMax) * 100}% + 3px)` : '3px'
                  }}
                  data-daily-volume-value={inspectedDaily}
                  data-day={previewDay}
                  aria-hidden="true"
                >{isTimeMode ? formatDurationMinutes(inspectedDaily) : formatNumber(inspectedDaily)}</span>
              )}

              <div className="daily-chart__x-axis" aria-hidden="true">
                {ticks.map((day) => (
                  <span
                    key={day}
                    className={day % 2 === 0 && day !== domainDays ? 'is-mobile-hidden' : undefined}
                    data-day={day}
                    style={{ left: `${(xPosition(day, domainDays) / VIEWBOX_WIDTH) * 100}%` }}
                  >{day}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="daily-chart__legend" aria-label="Legenda dos gráficos">
          <span><i className="daily-chart__legend-line is-current" aria-hidden="true" />Atual</span>
          <span><i className="daily-chart__legend-line is-previous" aria-hidden="true" />Anterior</span>
          <span><i className="daily-chart__legend-bar" aria-hidden="true" />{isTimeMode ? 'Tempo diário' : 'Volume'}</span>
        </div>
        </div>

        {hasHistoricalPages && !isTimeMode && (
          <p className="daily-chart__historical-note">A curva diária considera apenas páginas com data de leitura registrada.</p>
        )}
      </div>
    </section>
  );
};

export const DailyPagesComparisonChart = (props: DailyPagesComparisonChartProps) => (
  <DailyPagesComparisonChartContent
    key={`${props.referenceDate.getFullYear()}-${props.referenceDate.getMonth()}`}
    {...props}
  />
);
